import React, { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import Arrow from 'assets/svgs/down-arrow.svg';
import { motion, AnimatePresence } from 'framer-motion';
import { LocalContext } from 'context';
import { gridContainer, noPadding } from 'styles';
import Slots from './Slots';

const Session = ({ i, expanded, setExpanded, title, slots }) => {
  useEffect(() => {
    setExpanded(0);
  }, []);
  const isOpen = i === expanded;
  const { mobile, theme } = useContext(LocalContext);
  return (
    <StyledSession>
      {title && (
        <Grid>
          <Title
            type="button"
            theme={theme}
            open={isOpen}
            onClick={() => setExpanded(isOpen ? false : i)}>
            <SessionArrow open={isOpen} theme={theme} />
            {title}
          </Title>
        </Grid>
      )}

      {title && (
        <AnimatePresence initial={false}>
          {isOpen && (
            <>
              {!mobile && (
                <Grid theme={theme} layout>
                  {/* <Time>Time:</Time> */}
                  <Stream style={{ fontSize: '1rem', fontWeight: 'normal' }}>Livestream:</Stream>
                  <Speaker style={{ fontSize: '1rem', fontWeight: 'normal' }}>
                    Keynote Speaker:
                  </Speaker>
                </Grid>
              )}{' '}
              <SessionSlots
                layout
                theme={theme}
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, height: 'auto' },
                  collapsed: { opacity: 0, height: 0 }
                }}
                transition={{ duration: 0.25 }}>
                <Slots slots={slots} theme={theme} />
              </SessionSlots>
            </>
          )}
        </AnimatePresence>
      )}
      {!title && (
        <SessionSlots>
          <Slots slots={slots} theme={theme} />
        </SessionSlots>
      )}
    </StyledSession>
  );
};

const AgendaList = () => {
  const { theme } = useContext(LocalContext);

  const data = useStaticQuery(
    graphql`
      query {
        allMarkdownRemark(
          sort: { order: ASC, fields: [frontmatter___id] }
          filter: { fileAbsolutePath: { regex: "/(agenda)/" }, frontmatter: {} }
        ) {
          nodes {
            id
            frontmatter {
              id
              title
              slots {
                order
                time
                livestream
                speaker
              }
            }
          }
        }
      }
    `
  );

  const { nodes: sessions } = data.allMarkdownRemark;

  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <H3 theme={theme}>Agenda</H3>

      {sessions.map((session, i) => (
        <Session
          i={i}
          key={i}
          expanded={expanded}
          setExpanded={setExpanded}
          title={session.frontmatter.title}
          slots={session.frontmatter.slots}
        />
      ))}
    </>
  );
};

const H3 = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1.75rem;
  grid-column: 1/7;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.textColor};
  @media (min-width: 768px) {
    grid-column: 2/6;
  }
  @media (min-width: 1150px) {
    grid-column: 2/12;
  }
  @media (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const Grid = styled.div`
  ${gridContainer}
  margin-bottom: 0rem;
  ${noPadding};
  color: ${({ theme }) => theme.textColor};
  @media (min-width: 1150px) {
    margin-bottom: 0rem;
  }
  @media (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const Time = styled.div`
  grid-column: auto;

  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 1.3em;
  span {
    margin-right: 0.375rem;
  }
  @media (min-width: 48em) {
    font-size: 1rem;
  }
  @media (min-width: 72em) {
    grid-column: 1/2;

  }
`;

const Stream = styled.div`
  grid-column: 1/6;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.3em;
  @media (min-width: 48em) {
    font-size: 1rem;
  }
  /* @media (min-width: 72em) {
    grid-column: 1/7;
  } */
`;
const Speaker = styled.div`
  grid-column: 1/7;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 1.3em;
  @media (min-width: 48em) {
    font-size: 1rem;
  }
  @media (min-width: 72em) {
    grid-column: 6/10;
  }
`;

const Title = styled.button`
  transition: font-weight 0.25s ease-in-out;
  font-weight: ${({ open }) => (open ? 700 : 300)};
  font-size: 1.25rem;
  line-height: 1.3em;
  margin-bottom: 1rem;
  grid-column: 1/7;
  text-align: left;
  background: none;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.fg};
  display: flex;
  cursor: pointer;
  @media (min-width: 48em) {
    font-size: 1.5rem;
  }
`;

const SessionArrow = styled(Arrow)`
  margin-right: 1.25rem;
  transition: 0.5s;
  transform: ${({ open }) => (open ? 'scale(1, -1) translateY(-10px)' : 'none')};
  width: 1rem;
  min-width:1em;
  height: auto;
  path {
    stroke: ${({ open, theme }) => (open ? '#bdbdbd' : theme.fg)};
  }
  @media (min-width: 48em) {
    width: 1.5rem;
  }
`;

const SessionSlots = styled(motion.section)`
  ${gridContainer}
  margin:.5rem 0;
  ${noPadding};
  color: ${({ theme }) => theme.textColor};
`;

const StyledSession = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  grid-column: 1/7;
  overflow: hidden;
  @media (min-width: 48em) {
    grid-column: 2/6;
  }
  @media (min-width: 72em) {
    grid-column: 2/12;
  }
  @media (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

export default AgendaList;
